
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './src/App';
import './src/index.css';
import { ErrorBoundary } from './src/components/ErrorBoundary';
import { initGlobalBugCapture } from './src/services/bugReportService';
import { SignaturePadPage } from './src/components/crm/SignaturePadPage';
import { UnterweisungTeilnahmePage } from './src/components/sifa/public/UnterweisungTeilnahmePage';
import { SammelLinkPage } from './src/components/sifa/public/SammelLinkPage';
import { DatenschutzSeite } from './src/components/sifa/public/DatenschutzSeite';
import { parseSifaRoute, routenSchluessel, type SifaRoute } from './src/components/sifa/public/routenLogic';
import { KundenportalPage } from './src/components/sifa/portal/KundenportalPage';
import { EinweisungTeilnahmePage } from './src/components/sige-einweisung/public/EinweisungTeilnahmePage';
import { FreimeldungPage } from './src/components/freimeldung/public/FreimeldungPage';
import { GbuFreigabeSeite } from './src/components/sifa/gbu/public/GbuFreigabeSeite';
import { gbuFreigabeToken } from './src/components/sifa/gbu/public/gbuFreigabeSeiteLogic';
import { VorankUnterschriftSeite } from './src/components/UfsA/public/VorankUnterschriftSeite';
import { vorankRouteToken } from './src/components/UfsA/public/vorankUnterschriftLogic';
import { MaterialMeldenSeite } from './src/components/material/public/MaterialMeldenSeite';
import { materialRouteAusPfad, seitenSchluessel } from './src/services/material/materialMeldenLogic';
import { installiereNativeShims } from './src/services/nativeShims';
import { initDesignMode } from './src/design/glassMode';
import { KiRegelwerkHinweis } from './src/components/KiRegelwerkHinweis';

initGlobalBugCapture();
// Android-App: Downloads, neue Fenster und Drucken auf die Gerätewege umbiegen
// (docs/android-app-vertrag.md E14) — vor dem ersten Render, damit schon der
// erste Klick abgefangen wird. Im Browser kehrt der Aufruf sofort zurück.
// Eigener try/catch: ein Fehler hier darf nie das Laden der Seite verhindern,
// auch nicht auf den öffentlichen Seiten (#/freimeldung, #/sign, …).
try {
  installiereNativeShims();
} catch (e) {
  console.error(e);
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

// Oeffentliche Unterschriften-Seite (Smartphone via QR): #/sign/<token>?q=<AngebotsNr>
// wird OHNE Login gerendert — Rueckkanal ist die anon-RPC submit_signature.
// Mit ?u=1 gehoert der Token zur SiFa-Unterweisung (QR-Bruecke PC → Handy);
// dann schreibt die Seite ueber die anon-RPC sifa_submit_signature.
const signMatch = window.location.hash.match(/^#\/sign\/([A-Za-z0-9_-]+)(?:\?(.*))?$/);
const signParams = signMatch?.[2] ? new URLSearchParams(signMatch[2]) : null;
const signQuoteNumber = signParams?.get('q') || undefined;
const signRpc = signParams?.get('u') === '1' ? 'sifa_submit_signature' : 'submit_signature';

// Oeffentliche Seiten der SiFa-Unterweisung — ohne Login, nur anon-RPCs
// (docs/sifa-unterweisung-plan.md §5, docs/sifa-ausbau2-plan.md §3.3):
//   #/unterweisung/<linkToken>[?von=<sammelToken>]  Teilnehmerseite
//   #/unterweisung-test/<token>                     Testlauf (nichts wird gespeichert)
//   #/unterweisungen/<sammelToken>                  Sammel-Link (alle freigeschalteten)
//   #/datenschutz/unterweisung?l=…|?s=…|?t=…        Datenschutzhinweise
// Die Muster in routenLogic sind hinter dem Segment verankert —
// #/unterweisungen/ und #/unterweisung-test/ landen nie auf der Teilnehmerseite.
const sifaRoute = parseSifaRoute(window.location.hash);
const sifaSeite = (route: SifaRoute | null): React.ReactElement | null => {
  switch (route?.seite) {
    case 'teilnahme':
      return <UnterweisungTeilnahmePage linkToken={route.linkToken} vonSammel={route.von} />;
    case 'test':
      return <UnterweisungTeilnahmePage linkToken={route.token} modus="test" />;
    case 'sammel':
      return <SammelLinkPage token={route.token} />;
    case 'datenschutz':
      return <DatenschutzSeite quelle={route.quelle} />;
    default:
      return null;
  }
};

// Kundenbereich eines SiFa-Projekts: #/kundenportal/<token> — ohne Login,
// Token + sechsstellige PIN, nur anon-RPCs (supabase/sifa_kundenportal_rpc.sql).
const kundenportalMatch = window.location.hash.match(/^#\/kundenportal\/([A-Za-z0-9_-]+)\/?(?:\?.*)?$/);

// Oeffentliche Teilnehmerseite der Einweisung in den SiGe-Plan: #/einweisung/<linkToken>
// (QR auf Aushang, Plan und Handbuch) — ohne Login, nur anon-RPCs
// (docs/sige-einweisung-online-plan.md §4, supabase/sige_einweisung_online_rpc.sql).
const einweisungMatch = window.location.hash.match(/^#\/einweisung\/([A-Za-z0-9_-]+)\/?(?:\?.*)?$/);

// Oeffentliche Melderseite zum Begehungsbericht: #/freimeldung/<token> — der QR
// steht NUR im PDF, der Empfaenger meldet seinen Mangel ohne Login als behoben
// (docs/freimeldung-online-plan.md §E, supabase/report_freimeldung_rpc.sql).
const freimeldungMatch = window.location.hash.match(/^#\/freimeldung\/([A-Za-z0-9_-]+)\/?(?:\?.*)?$/);

// Oeffentliche Freigabeseite einer Gefaehrdungsbeurteilung: #/gbu-freigabe/<token>
// — der Unternehmer unterschreibt die eingefrorene Fassung ohne Login; der
// Link gilt fuer eine Fassung, einmal, 14 Tage (docs/sifa-gbu-plan.md §8,
// supabase/sifa_gbu_rpc.sql). Das Muster steht in gbuFreigabeSeiteLogic (getestet).
const gbuFreigabeTokenAusHash = gbuFreigabeToken(window.location.hash);

// Oeffentliche Unterschriftsseite der Vorankuendigung: #/vorankuendigung/<token>
// — QR und Link stehen im Begleitblatt des PDF-Exports; der Bauherr prueft die
// eingefrorene Fassung ohne Login, unterschreibt (Handy, Maus, Bild) oder
// meldet eine Korrektur. Gelesen wird ueber die anon-RPC
// vorank_unterschrift_public, geschrieben NUR ueber /api/vorank-unterschrift
// (docs/vorankuendigung-unterschrift-plan.md §6, supabase/vorank_unterschrift_rpc.sql).
// Das Muster steht in vorankUnterschriftLogic (getestet); ein verstuemmelter
// Token zeigt „nicht gueltig“ statt der Anmeldemaske.
const vorankTokenAusHash = vorankRouteToken(window.location.hash);

// Oeffentliche Meldeseite der Materialverwaltung: <origin>/m/<token> — als
// PFAD, nicht im Hash (QR auf der gedruckten Nachbestellkarte, kurz und
// endgueltig; docs/materialverwaltung-plan.md). Ohne Login; die Karte kommt
// ueber die anon-RPC material_karte_public, gemeldet wird ueber
// /api/material-melden. Ein Pfad /m/… ohne gueltigen Token zeigt die Seite
// im Zustand „Karte ungueltig". Steht vorn: der Pfad entscheidet vor dem Hash.
const materialRoute = materialRouteAusPfad(window.location.pathname);

const oeffentlicheSeite = materialRoute
  ? <MaterialMeldenSeite token={materialRoute.token} />
  : signMatch
  ? <SignaturePadPage token={signMatch[1]} quoteNumber={signQuoteNumber} rpc={signRpc} />
  : sifaRoute
    ? sifaSeite(sifaRoute)
    : kundenportalMatch
      ? <KundenportalPage token={kundenportalMatch[1]} />
      : einweisungMatch
        ? <EinweisungTeilnahmePage linkToken={einweisungMatch[1]} />
        : freimeldungMatch
          ? <FreimeldungPage token={freimeldungMatch[1]} />
          : gbuFreigabeTokenAusHash
            ? <GbuFreigabeSeite token={gbuFreigabeTokenAusHash} />
            : vorankTokenAusHash
              ? <VorankUnterschriftSeite token={vorankTokenAusHash} />
              : null;

// Optik „Glas" (src/design/glassMode.ts): Schalter aus Adresse oder Browser
// lesen, bevor das Dashboard zum ersten Mal zeichnet — NUR fuer die App.
// Oeffentliche Seiten (Meldeseite, Unterschrift, Kundenportal …) bleiben
// klassisch, auch im Browser eines Mitarbeiters, der Glas gewaehlt hat.
if (!oeffentlicheSeite) {
  try {
    initDesignMode();
  } catch (e) {
    console.error(e);
  }
}

// Die Seite wird beim Laden EINMAL gewaehlt. Wechselt auf einer oeffentlichen
// Seite der Pfad im Hash (Sammel-Link → Unterweisung, Ruecklink, Browser-
// Zurueck), wuerde sonst die alte Seite stehen bleiben — dann neu laden, damit
// oben die passende Seite gewaehlt wird. Ein Wechsel nur der Abfrage (?…) laedt
// nicht neu, ausser bei den Datenschutzhinweisen: dort ist die Abfrage die
// Quelle (routenSchluessel). Auf der Material-Meldeseite waehlt der Pfad die
// Seite; ein Hash-Wechsel aendert dort nichts (seitenSchluessel). Die
// angemeldete App ist nicht betroffen (kein Listener).
if (oeffentlicheSeite) {
  const aktuellerSchluessel = (): string =>
    seitenSchluessel(window.location.pathname, routenSchluessel(window.location.hash));
  const startSchluessel = aktuellerSchluessel();
  window.addEventListener('hashchange', () => {
    if (aktuellerSchluessel() !== startSchluessel) window.location.reload();
  });
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      {oeffentlicheSeite ?? (
        <>
          <App />
          <KiRegelwerkHinweis />
        </>
      )}
    </ErrorBoundary>
  </React.StrictMode>
);
